import { useState } from "react";
import dashboard_icon from '../../assets/img/dashboard.svg';
import help_icon from '../../assets/img/help-white.svg';
import faq_icon from '../../assets/img/faq-outline.svg';
import feedback_icon from '../../assets/img/feedback-outline.svg';
import groups_icon from '../../assets/img/user-groups-outline.svg';
import video_icon from '../../assets/img/video-white.svg';
import logout_icon from '../../assets/img/logout.svg';
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";
import { Button, Form, FormGroup } from "react-bootstrap";
import Select from "react-select";
import { Overlay } from "../elements/Overlay";
import { emptyFeedback } from "../interfaces/Feedback";
import { handleApiError, safeJsonParse } from "../../utils/api";

const feedbackSectionOptions = [
    { value: 'login', label: 'Login' },
    { value: 'dashboard', label: 'Dashboard' },
    { value: 'tasks', label: 'Tasks' },
    { value: 'pretasks', label: 'Pretasks' },
    { value: 'postasks', label: 'Postasks' },
    { value: 'task-groups', label: 'Task groups' },
    { value: 'task-roles', label: 'Task roles' },
    { value: 'task-prompts', label: 'Task prompts' },
    { value: 'text-analyzer', label: 'Text analyzer' },
    { value: 'my-design', label: 'My design' },
    { value: 'students-site', label: 'Shared with students' },
    { value: 'teachers-site', label: 'Shared with teachers' },
    { value: 'help', label: 'Help system' }
];

interface NavigationProps {
    closeMobileMenu: () => void;
}

export default function Navigation({ closeMobileMenu }: NavigationProps) {
    const navigate = useNavigate();
    const [showFeedbackForm, setShowFeedbackForm] = useState(false);
    const [feedbackSection, setFeedbackSection] = useState('');

    const handleShowFeedback = (e: React.MouseEvent) => {
        e.preventDefault();
        setShowFeedbackForm(true);
    };

    const handleCloseFeedback = () => {
        setShowFeedbackForm(false);
        setFeedbackSection('');
    };

    const handleFeedbackSubmit = (event: any) => {
        event.preventDefault();
        const newFeedback = Object.assign({}, emptyFeedback);
        newFeedback.user._id = localStorage.getItem("userId") + '';
        newFeedback.section = feedbackSection;
        newFeedback.issue = event.target.subject.value;
        newFeedback.body = event.target.body.value;
        newFeedback.state = 0;
        const requestOptionsPost = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            },
            body: JSON.stringify(newFeedback)
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/feedback', requestOptionsPost)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (!data) return;
                handleCloseFeedback();
                alert(t('Thanks for your feedback.'));
            });
    };

    const handleLogout = async (event: any) => {
        event.preventDefault();

        // Invalidar sesión en el servidor
        const token = localStorage.getItem('token');
        if (token) {
            try {
                await fetch(import.meta.env.VITE_API_URL + '/v1/user/logout', {
                    method: 'POST',
                    headers: {
                        'Authorization': 'Bearer ' + token
                    }
                });
            } catch (error) {
                console.error('Error during logout:', error);
            }
        }

        // Limpiar datos locales
        localStorage.removeItem('token');
        localStorage.setItem('userId', '');
        localStorage.setItem('userRole', '');
        localStorage.setItem('isAdmin', '');
        closeMobileMenu();
        navigate('/');
    }

    const handleLinkClick = () => {
        closeMobileMenu();
    }

    return (
        <div className="navigation">
            <button className="close-mobile-btn" onClick={closeMobileMenu} aria-label="Close menu">
                <i className="bi bi-x-lg"></i>
            </button>
            <ul className="nav-list primary-group">
                <li>
                    <Link className="nav-link active" to="/dashboard" title={t("Dashboard")} onClick={handleLinkClick}>
                        <img src={dashboard_icon} alt="Dashboard"/>
                        <span>{t("Dashboard")}</span>
                    </Link>
                </li>
                <li>
                    <Link className="nav-link user-groups" to="/users-groups" title={t("User groups")} onClick={handleLinkClick}>
                        <img src={groups_icon} alt="User groups"/>
                        <span>{t("User groups")}</span>
                    </Link>
                </li>
                <li>
                    <Link className="nav-link" to="/video-tutorials" title={t("Video tutorials")} onClick={handleLinkClick}>
                        <img src={video_icon} alt="Video tutorials"/>
                        <span>{t("Video tutorials")}</span>
                    </Link>
                </li>
                <li>
                    <Link className="nav-link" to="/help" title={t("Help")} onClick={handleLinkClick}>
                        <img src={help_icon} alt="Help"/>
                        <span>{t("Help")}</span>
                    </Link>
                </li>
                <li>
                    <Link className="nav-link" to="/faqs" title="FAQs" onClick={handleLinkClick}>
                        <img src={faq_icon} alt="FAQs"/>
                        <span>FAQs</span>
                    </Link>
                </li>
                <li>
                    <a href="#" className="nav-link" title={t("Feedback")} onClick={handleShowFeedback}>
                        <img src={feedback_icon} alt="Feedback"/>
                        <span>{t("Feedback")}</span>
                    </a>
                </li>
            </ul>
            <ul className="nav-list logout-group">
                <li>
                    <a href="#" onClick={handleLogout} className="nav-link logout" title={t("Log out")}>
                        <img src={logout_icon} alt="Log out"/>
                        <span>{t("Log out")}</span>
                    </a>
                </li>
            </ul>
            {showFeedbackForm && (
                <Overlay title={t('Add new Feedback')} onClose={handleCloseFeedback} className="admin-add-feedback-container">
                    <div className="feedback-form-container">
                        <Form onSubmit={handleFeedbackSubmit}>
                            <FormGroup className="form-field field-section" controlId="section">
                                <Form.Label aria-required={true}>{t('Section')}</Form.Label>
                                <Select
                                    options={feedbackSectionOptions.map((o: any) => ({ value: o.value, label: t(o.label) }))}
                                    onChange={(e: any) => setFeedbackSection(e.value)}
                                    required
                                />
                                <div className="description">
                                    {t('Select the functionality of the application about which you want to comment')}
                                </div>
                            </FormGroup>
                            <FormGroup className="form-field field-subject" controlId="subject">
                                <Form.Label aria-required={true}>{t('Subject')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    required={true}
                                    name="subject"
                                    placeholder={t('Write the subject of your comment')}
                                />
                            </FormGroup>
                            <FormGroup className="form-field field-body" controlId="body">
                                <Form.Label aria-required={true}>{t('Comment')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    placeholder={t('Leave your comments here')}
                                    required={true}
                                    rows={5}
                                />
                            </FormGroup>
                            <div className="actions">
                                <Button type="submit" variant="primary">{t("Save")}</Button>
                            </div>
                        </Form>
                    </div>
                </Overlay>
            )}
        </div>
    );
}